import { useState } from 'react';
import { message } from 'antd';
import { OntologyApi, type OntologyDetail } from '../api';

export const useOntologyDetail = () => {
  const [ontologyDetail, setOntologyDetail] = useState<OntologyDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // 加载本体详情数据
  const loadOntologyDetail = async (ontologyId: number) => {
    try {
      setLoadingDetail(true);
      console.log('🚀 加载本体详情:', ontologyId);
      
      const detail = await OntologyApi.getOntologyDetail(ontologyId);
      setOntologyDetail(detail);
      
      console.log('✅ 本体详情加载成功:', detail);
    } catch (error: any) {
      console.error('❌ 加载本体详情失败:', error);
      message.error(`加载本体详情失败：${error.message || '未知错误'}`);
    } finally {
      setLoadingDetail(false);
    }
  };

  return {
    ontologyDetail,
    setOntologyDetail,
    loadingDetail,
    loadOntologyDetail,
  };
};

export const useEditableField = (
  ontologyDetail: OntologyDetail | null,
  loadOntologyDetail: (id: number) => Promise<void>,
  onNameUpdated?: (newName: string) => void // 名称更新后的回调，用于同步左侧树列表
) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempEditValue, setTempEditValue] = useState('');
  const [tempTagValue, setTempTagValue] = useState('');

  // 处理基础信息编辑
  const handleEditField = (field: string, currentValue: string) => {
    setEditingField(field);
    setTempEditValue(currentValue);
  };

  const handleSaveField = async (field: string) => {
    if (!ontologyDetail) return;
    
    try {
      const updateData: Partial<OntologyDetail> = {};
      
      if (field === 'name') {
        updateData.name = tempEditValue;
      } else if (field === 'definition') {
        updateData.definition = tempEditValue;
      } else if (field === 'description') {
        updateData.description = tempEditValue;
      } else if (field === 'tags') {
        setEditingField(null);
        setTempEditValue('');
        return;
      } else {
        setEditingField(null);
        setTempEditValue('');
        return;
      }
      
      await OntologyApi.updateOntologyInfo(ontologyDetail.id, updateData);
      await loadOntologyDetail(ontologyDetail.id);

      // 如果修改了名称，通知父组件更新左侧树列表
      if (field === 'name' && onNameUpdated)
        onNameUpdated(tempEditValue);

      setEditingField(null);
      setTempEditValue('');
      message.success('修改成功');
    } catch (error: any) {
      message.error(`保存失败：${error.message || '未知错误'}`);
    }
  };

  const handleCancelFieldEdit = () => {
    setEditingField(null);
    setTempEditValue('');
    setTempTagValue('');
  };

  // 标签编辑相关函数
  const handleAddTag = async () => {
    if (!ontologyDetail || !tempTagValue.trim() || (ontologyDetail.tags || []).includes(tempTagValue.trim())) {
      return;
    }
    
    try {
      const updatedTags = [...(ontologyDetail.tags || []), tempTagValue.trim()];
      await OntologyApi.updateOntologyInfo(ontologyDetail.id, { tags: updatedTags });
      await loadOntologyDetail(ontologyDetail.id);
      
      setTempTagValue('');
      message.success('标签添加成功');
    } catch (error: any) {
      message.error(`添加标签失败：${error.message || '未知错误'}`);
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    if (!ontologyDetail) return;
    
    try {
      const updatedTags = (ontologyDetail.tags || []).filter(tag => tag !== tagToRemove);
      await OntologyApi.updateOntologyInfo(ontologyDetail.id, { tags: updatedTags });
      await loadOntologyDetail(ontologyDetail.id);
      
      message.success('标签删除成功');
    } catch (error: any) {
      message.error(`删除标签失败：${error.message || '未知错误'}`);
    }
  };

  return {
    editingField,
    setEditingField,
    tempEditValue,
    setTempEditValue,
    tempTagValue,
    setTempTagValue,
    handleEditField,
    handleSaveField,
    handleCancelFieldEdit,
    handleAddTag,
    handleRemoveTag,
  };
};